import { z } from 'zod';

/**
 * Shared field constraints. Password is omitted here and applied per-mode:
 * required on create, optional (and trimmed away when empty) on edit.
 */
const baseFields = {
  full_name: z
    .string()
    .trim()
    .min(2, 'staff.form.errors.fullNameRequired')
    .max(120, 'staff.form.errors.tooLong'),
  username: z
    .string()
    .trim()
    .min(3, 'staff.form.errors.usernameMin')
    .max(40, 'staff.form.errors.tooLong')
    .regex(/^[a-zA-Z0-9_.-]+$/u, 'staff.form.errors.usernameInvalid'),
  phone_number: z
    .string()
    .trim()
    .max(20, 'staff.form.errors.tooLong')
    .optional()
    .or(z.literal('')),
  role_id: z
    .string()
    .min(1, 'staff.form.errors.roleRequired'),
};

export const staffCreateSchema = z.object({
  ...baseFields,
  password: z
    .string()
    .min(6, 'staff.form.errors.passwordMin')
    .max(128, 'staff.form.errors.tooLong'),
});

/**
 * Edit schema: password is optional. Empty string is treated as "leave
 * unchanged" — when present it must satisfy the same length rule.
 */
export const staffEditSchema = z.object({
  ...baseFields,
  password: z
    .string()
    .max(128, 'staff.form.errors.tooLong')
    .refine((v) => v === '' || v.length >= 6, 'staff.form.errors.passwordMin')
    .optional()
    .or(z.literal('')),
});

export type StaffFormValues = {
  full_name: string;
  username: string;
  password: string;
  phone_number: string;
  role_id: string;
};

export const emptyStaffFormValues: StaffFormValues = {
  full_name: '',
  username: '',
  password: '',
  phone_number: '',
  role_id: '',
};
